import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { forwardRef, useMemo, useState } from 'react';
import { TextField as MuiTextField } from '@material-ui/core';
import cn from 'classnames';
import { ChevronDown } from '../../icons';
import { useDevice } from '../../providers/Device';
import { useBiAction } from '../BiTracking';
import { BottomSheetContentLayout } from '../BottomSheet';
import { Checkbox } from '../Checkbox';
import { Box, Row } from '../Layout';
import { MenuFooter } from '../Menu/MenuFooter';
import { menuFooterItemsFromOptions } from '../Menu/MenuFooter.utils';
import { useSheetStack } from '../OverlayContext/useSheetStack';
import { Tooltip } from '../Tooltip';
import { skinToColor, useInlineSelectStyles } from './InlineSelect.styles';
const isMultipleProps = (props)=>props.multiple === true;
const defaultRenderMultipleValue = (values, children)=>{
    const labels = [];
    React.Children.forEach(children, (child)=>{
        var _childProps_label;
        if (!/*#__PURE__*/ React.isValidElement(child)) return;
        const childProps = child.props;
        if (!values.includes(childProps.value)) return;
        if (labels.length > 0) labels.push(', ');
        labels.push(/*#__PURE__*/ _jsx(React.Fragment, {
            children: (_childProps_label = childProps.label) !== null && _childProps_label !== void 0 ? _childProps_label : childProps.children
        }, String(childProps.value)));
    });
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: labels
    });
};
function InlineSelectBottomSheet(props) {
    const { children, value, onChange, isSelected, footer } = props;
    const { popBottomSheetPage } = useSheetStack();
    return /*#__PURE__*/ _jsxs(BottomSheetContentLayout, {
        children: [
            React.Children.map(children, (child)=>{
                if (!/*#__PURE__*/ React.isValidElement(child)) return child;
                const childProps = child.props;
                return /*#__PURE__*/ React.cloneElement(child, _object_spread_props(_object_spread({}, childProps), {
                    selected: isSelected ? isSelected(childProps.value, value) : childProps.value === value,
                    onClick: ()=>{
                        onChange === null || onChange === void 0 ? void 0 : onChange({
                            target: {
                                value: childProps.value
                            }
                        });
                        popBottomSheetPage();
                    }
                }));
            }),
            footer
        ]
    });
}
function decorateWithCheckboxes(children, isCheckedFor, onItemClick) {
    return React.Children.map(children, (child)=>{
        if (!/*#__PURE__*/ React.isValidElement(child)) return child;
        const childProps = child.props;
        const isChecked = isCheckedFor(childProps.value);
        return /*#__PURE__*/ React.cloneElement(child, _object_spread_props(_object_spread(_object_spread_props(_object_spread({}, childProps), {
            selected: isChecked
        }), onItemClick != null ? {
            onClick: ()=>onItemClick(childProps.value)
        } : {}), {
            children: /*#__PURE__*/ _jsxs(Row, {
                gap: 8,
                alignItems: "center",
                children: [
                    /*#__PURE__*/ _jsx(Checkbox, {
                        noBi: true,
                        checked: isChecked
                    }),
                    childProps.children
                ]
            })
        }));
    });
}
function InlineSelectMultipleBottomSheet(props) {
    const { children, initialValues, onSave, saveButtonLabel } = props;
    const { hideBottomSheet } = useSheetStack();
    const [tempValues, setTempValues] = useState(initialValues);
    const toggle = (value)=>{
        setTempValues((prev)=>prev.includes(value) ? prev.filter((v)=>v !== value) : [
                ...prev,
                value
            ]);
    };
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        footer: {
            primaryAction: {
                text: saveButtonLabel !== null && saveButtonLabel !== void 0 ? saveButtonLabel : 'Save',
                onClick: ()=>{
                    onSave(tempValues);
                    hideBottomSheet();
                },
                // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- Save commits the selection; onSave -> handleMultipleSave already logs the commit as `inline_select`/`select`, so a button bi would double-fire the same gesture
                noBi: true
            },
            paddingTop: 8
        },
        children: decorateWithCheckboxes(children, (value)=>tempValues.includes(value), toggle)
    });
}
// See note on BaseTextField — forwardRef + MUI types collapses BiProps' discriminated union.
const InlineSelectImpl = /*#__PURE__*/ forwardRef(function InlineSelect(props, ref) {
    var _props_InputProps;
    const isMultiple = isMultipleProps(props);
    const { children, className, required, testId, inputProps, menuMaxHeight = 300, menuMaxWidth, skin = 'standard', SelectProps, helperText, value, onChange, placeholder, newItemOption, promotionalActionOption, error, bi, // WHY: BiProps union remnant — consumer supplies bi; strip noBi from rest so it never spreads onto MuiTextField's DOM input
    noBi: _noBi } = props, rest = _object_without_properties(props, [
        "children",
        "className",
        "required",
        "testId",
        "inputProps",
        "menuMaxHeight",
        "menuMaxWidth",
        "skin",
        "SelectProps",
        "helperText",
        "value",
        "onChange",
        "placeholder",
        "newItemOption",
        "promotionalActionOption",
        "error",
        "bi",
        "noBi"
    ]);
    const [footerMenuOpen, setFooterMenuOpen] = useState(false);
    const isSelected = isMultiple ? undefined : props.isSelected;
    const renderMultipleValue = isMultiple ? props.renderValue : undefined;
    const saveButtonLabel = isMultiple ? props.saveButtonLabel : undefined;
    const logBiAction = useBiAction();
    const { isMobileDevice } = useDevice();
    const logSelectionBi = (selectedValue)=>{
        if ((bi === null || bi === void 0 ? void 0 : bi.name) == null) return;
        logBiAction({
            entityType: 'inline_select',
            entityName: bi.name,
            actionType: 'select',
            testId: testId !== null && testId !== void 0 ? testId : '',
            data: _object_spread_props(_object_spread({}, bi.data), {
                selected_value: JSON.stringify(selectedValue)
            })
        });
    };
    const handleChange = (e)=>{
        logSelectionBi(e.target.value);
        if (isMultiple) {
            const arrayValue = e.target.value;
            onChange(arrayValue);
        } else {
            onChange === null || onChange === void 0 ? void 0 : onChange(e);
        }
    };
    const handleMultipleSave = (values)=>{
        logSelectionBi(values);
        onChange(values);
    };
    const { pushBottomSheetPage } = useSheetStack();
    const overrides = useInlineSelectStyles({
        menuMaxHeight,
        menuMaxWidth,
        color: skinToColor[skin],
        isMobileDevice
    });
    const IconComponent = useMemo(()=>(param)=>{
            let { className } = param;
            return /*#__PURE__*/ _jsx(ChevronDown, {
                size: 16,
                testId: testId && "".concat(testId, "-indicator"),
                className: cn(className, overrides.icon)
            });
        }, [
        overrides.icon,
        testId
    ]);
    function openMobileSheet() {
        if (isMultiple) {
            pushBottomSheetPage({
                content: /*#__PURE__*/ _jsx(InlineSelectMultipleBottomSheet, {
                    initialValues: value,
                    onSave: handleMultipleSave,
                    saveButtonLabel: saveButtonLabel,
                    children: children
                })
            });
        } else {
            pushBottomSheetPage({
                content: /*#__PURE__*/ _jsx(InlineSelectBottomSheet, {
                    value: value,
                    onChange: handleChange,
                    isSelected: isSelected,
                    footer: footerNode,
                    children: children
                })
            });
        }
    }
    const dismissMenuThen = (action)=>function() {
            for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
                args[_key] = arguments[_key];
            }
            setFooterMenuOpen(false);
            return action(...args);
        };
    const withMenuDismiss = (item)=>item.variant === 'contextualAction' ? _object_spread_props(_object_spread({}, item), {
            props: _object_spread_props(_object_spread({}, item.props), {
                onClick: item.props.onClick && dismissMenuThen(item.props.onClick),
                onMouseDown: item.props.onMouseDown && dismissMenuThen(item.props.onMouseDown)
            })
        }) : _object_spread_props(_object_spread({}, item), {
            props: _object_spread_props(_object_spread({}, item.props), {
                onClick: item.props.onClick && dismissMenuThen(item.props.onClick),
                onMouseDown: item.props.onMouseDown && dismissMenuThen(item.props.onMouseDown)
            })
        });
    const newItemOptions = newItemOption == null ? [] : Array.isArray(newItemOption) ? newItemOption : [
        newItemOption
    ];
    const hasFooter = newItemOptions.length > 0 || promotionalActionOption != null;
    const footerItems = menuFooterItemsFromOptions({
        newItemOptions,
        promotionalAction: promotionalActionOption,
        testIds: {
            newItemOption: (index)=>"".concat(testId !== null && testId !== void 0 ? testId : 'inline-select', "-footer-new-item-").concat(index),
            promotionalAction: "".concat(testId !== null && testId !== void 0 ? testId : 'inline-select', "-footer-promotional-action")
        },
        runNewItemOption: (option)=>option.onClick(),
        runPromotionalAction: (action)=>action.onClick()
    });
    const footerNode = hasFooter ? /*#__PURE__*/ _jsx(MenuFooter, {
        items: footerItems.map(withMenuDismiss),
        closeBottomSheetOnAction: true
    }) : null;
    const footerControlledMenuProps = hasFooter && !isMobileDevice ? {
        open: footerMenuOpen,
        onOpen: ()=>setFooterMenuOpen(true),
        onClose: ()=>setFooterMenuOpen(false)
    } : {};
    const mobileSelectProps = isMobileDevice ? {
        open: false,
        onOpen: openMobileSheet
    } : {};
    const decoratedChildren = isMultiple ? decorateWithCheckboxes(children, (val)=>value.includes(val)) : children;
    const multipleRenderValue = isMultiple ? (selected)=>{
        const selectedArray = selected;
        if (renderMultipleValue != null) return renderMultipleValue(selectedArray);
        if (selectedArray.length === 0) return /*#__PURE__*/ _jsx("span", {
            className: overrides.selectPlaceholder,
            children: placeholder !== null && placeholder !== void 0 ? placeholder : ''
        });
        return defaultRenderMultipleValue(selectedArray, children);
    } : undefined;
    const textField = /*#__PURE__*/ _jsxs(MuiTextField, _object_spread_props(_object_spread({
        innerRef: ref,
        value: value,
        onChange: handleChange,
        placeholder: placeholder,
        error: error
    }, rest), {
        "data-testid": testId,
        select: true,
        className: cn(className, overrides.root),
        variant: "standard",
        size: "small",
        helperText: undefined,
        InputProps: {
            ref: (_props_InputProps = props.InputProps) === null || _props_InputProps === void 0 ? void 0 : _props_InputProps.ref,
            required,
            classes: {
                root: overrides.inputRoot,
                input: overrides.inputInput
            }
        },
        SelectProps: _object_spread({
            IconComponent,
            MenuProps: _object_spread_props(_object_spread({
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                },
                transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                },
                getContentAnchorEl: null,
                classes: {
                    paper: overrides.menu
                }
            }, hasFooter ? {
                PaperProps: {
                    style: {
                        minWidth: 280
                    }
                },
                MenuListProps: {
                    style: {
                        paddingBottom: 0
                    }
                }
            } : {}), {
                ['data-testid']: testId && "".concat(testId, "-backdrop")
            }),
            SelectDisplayProps: {
                ['data-testid']: testId && "".concat(testId, "-select"),
                ['aria-invalid']: error === true
            },
            multiple: isMultiple,
            displayEmpty: isMultiple ? true : undefined,
            renderValue: multipleRenderValue
        }, footerControlledMenuProps, SelectProps, mobileSelectProps),
        inputProps: _object_spread({
            ['data-testid']: testId && "".concat(testId, "-input")
        }, inputProps),
        children: [
            decoratedChildren,
            !isMobileDevice && footerNode != null && /*#__PURE__*/ _jsx(Box, {
                style: {
                    marginTop: 8
                },
                children: footerNode
            })
        ]
    }));
    return helperText ? /*#__PURE__*/ _jsx(Tooltip, {
        placement: "top",
        title: helperText,
        testId: testId && "".concat(testId, "-tooltip"),
        children: textField
    }) : textField;
});
export const InlineSelect = InlineSelectImpl;
